// Keeps the message composer's promise honest as its choices change:
// which side of the filters it goes to, and whether teammates' contacts
// count. The server renders the opening state with the same wording
// (MessageComposerView.sendLabel and reachNote); this follows changes.
//
// Targets: [data-message-counts], a form carrying its field names
// (data-to-field, data-to-others, data-reach-field) and its counts
// (data-noun, data-nouns, data-count, data-count-own, data-others,
// data-others-own, and data-max, the cap on one message).

import { counted as countedNoun, grouped } from "./count_phrase.js";

let wired = false;

function counted(form, n) {
  return countedNoun(n, form.dataset.noun, form.dataset.nouns);
}

function update(form) {
  const { toField, toOthers, reachField } = form.dataset;
  const others =
    form.querySelector(`[name="${toField}"]:checked`)?.value === toOthers;
  const widened =
    form.querySelector(`[name="${reachField}"]`)?.checked === true;
  const total = Number(others ? form.dataset.others : form.dataset.count);
  const own = Number(others ? form.dataset.othersOwn : form.dataset.countOwn);
  const reached = widened ? total : own;
  const max = Number(form.dataset.max);

  let outcome;
  if (reached > max) {
    outcome = `Together that's ${grouped(reached)}, past the ${grouped(max)} one message reaches.`;
  } else if (reached === total) {
    outcome = "All of them get it.";
  } else if (reached === 0) {
    outcome = "None are assigned to you, so nobody gets it.";
  } else if (reached === 1) {
    outcome = "The 1 assigned to you gets it.";
  } else {
    outcome = `The ${grouped(reached)} assigned to you get it.`;
  }

  const note = form.querySelector("[data-message-reach-note]");
  if (note) note.textContent = ` ${outcome}`;

  const blocked = reached === 0 || reached > max;
  let label = `Send to ${counted(form, reached)}`;
  if (reached === 0) label = "Nobody to send to";
  if (reached > max) label = "Too many for one message";

  const send = form.querySelector("[data-message-send-label]");
  if (send) {
    send.textContent = label;
    // htmx disables Send while a request is out; leave that alone, or a
    // change mid-send would re-arm it and send twice.
    if (!form.classList.contains("htmx-request")) send.disabled = blocked;
  }

  form.dispatchEvent(new CustomEvent("message:reach", { detail: { reached } }));

  // Focus stays on the choice, so the new promise is announced here.
  const status = form.querySelector("[data-message-reach-status]");
  if (status) {
    status.textContent = blocked
      ? `${label}: ${outcome.toLowerCase()}`
      : `Sends to ${counted(form, reached)}`;
  }
}

export function initMessageRecipients() {
  if (wired) return;
  wired = true;

  // One delegated listener covers every composer htmx swaps in.
  document.addEventListener("change", (e) => {
    const form = e.target.closest?.("[data-message-counts]");
    if (form) update(form);
  });

  // htmx re-enables Send once a refused request returns, whatever the
  // choices now promise; set it again from them.
  document.addEventListener("htmx:afterRequest", (e) => {
    const form = e.target.closest?.("[data-message-counts]");
    if (form) update(form);
  });
}
